import React, { type ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { AuthContext } from '../../contexts/auth';
import { auth } from '../../api/firebase/config';
import { getSamlRequirement } from '../../api/firebase/function/security/securitySamlFunction';
import { SAML_SESSION_TTL_HOURS } from '../../constants/enum';
import { isOperatorStaff } from '../../utils/permission';
import { isSamlProviderId } from '../../utils/samlUser';
import { isSecurityGatePublicPath } from '../../utils/securityGatePublicPath';
import SamlSignInRequired from '../pages/common/SamlSignInRequired';

const SECONDS_PER_HOUR = 3600;
const MS_PER_SECOND = 1000;

/**
 * SAML 強制ゲート（#2983・PRD §5-2 / §10 R3）。
 * - SAML 必須モードのテナントで非 SAML セッションのメンバーには、通常画面の代わりに
 *   SSO 再ログイン要求画面（SamlSignInRequired）を表示する
 * - SAML セッションは TTL（8h）超過で自動ログアウトする（IdP 側デプロビジョニングの
 *   遮断緩和策。サーバー側でもリフレッシュトークンを失効させる）
 *
 * 判定はサーバー側（getSamlRequirement callable）で行い、ログインユーザーが変わった
 * タイミングで実行する。運営スタッフは対象外。判定不能時は表示を許可する
 * （MfaEnforcementGate / IpRestrictionGate と同パターン）。
 */
const SamlEnforcementGate = ({ children }: { children: ReactNode }): JSX.Element => {
  const { user } = useContext(AuthContext);
  const { pathname } = useLocation();
  const [needsSsoRelogin, setNeedsSsoRelogin] = useState(false);

  const uid = user?.uid ?? null;
  const isOperator = isOperatorStaff(user?.role);

  // SAML 必須モードの判定（サーバー判定・ノンブロッキング）
  useEffect(() => {
    if (!uid || isOperator) {
      setNeedsSsoRelogin(false);
      return undefined;
    }
    let cancelled = false;
    getSamlRequirement()
      .then(result => {
        if (cancelled) return;
        setNeedsSsoRelogin(result.needs_sso_relogin);
        if (result.session_expired) {
          // サーバー側でリフレッシュトークンは失効済み。クライアントも即ログアウト
          void signOut(auth).catch(() => undefined);
        }
      })
      .catch(() => {
        // 判定不能時は表示を許可（設定変更時のロックアウトを避ける）
        if (!cancelled) setNeedsSsoRelogin(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, isOperator]);

  // SAML セッションの TTL(8h) をクライアント側でも監視し、期限到来で自動ログアウトする
  useEffect(() => {
    if (!uid) return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    void auth.currentUser
      ?.getIdTokenResult()
      .then(tokenResult => {
        if (cancelled) return;
        const signInProvider = tokenResult.signInProvider ?? '';
        if (!isSamlProviderId(signInProvider)) return;
        const authTimeSeconds = Number(tokenResult.claims.auth_time ?? 0);
        const expiresAtMs =
          (authTimeSeconds + SAML_SESSION_TTL_HOURS * SECONDS_PER_HOUR) * MS_PER_SECOND;
        const remainingMs = expiresAtMs - Date.now();
        if (remainingMs <= 0) {
          void signOut(auth).catch(() => undefined);
          return;
        }
        timer = setTimeout(() => {
          void signOut(auth).catch(() => undefined);
        }, remainingMs);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [uid]);

  if (uid && !isOperator && needsSsoRelogin && !isSecurityGatePublicPath(pathname)) {
    return <SamlSignInRequired />;
  }

  return <>{children}</>;
};

export default SamlEnforcementGate;
